/**
 * Removes an RTF group that starts at `start` (index of '{'), handling nested braces.
 */
function removeGroupAt(rtf, start) {
  let depth = 0;
  for (let i = start; i < rtf.length; i += 1) {
    const ch = rtf[i];
    if (ch === "{") depth += 1;
    else if (ch === "}") {
      depth -= 1;
      if (depth === 0) {
        return rtf.slice(0, start) + rtf.slice(i + 1);
      }
    }
  }
  return rtf.slice(0, start);
}

/**
 * Strip destination/header groups that should not appear in plain text.
 */
function stripRtfHeaderGroups(rtf) {
  const headers = [
    "\\fonttbl",
    "\\colortbl",
    "\\stylesheet",
    "\\info",
    "\\*\\generator",
    "\\*\\panose",
  ];

  let text = rtf;
  let changed = true;
  while (changed) {
    changed = false;
    for (const header of headers) {
      const idx = text.indexOf(header);
      if (idx === -1) continue;
      // Walk back to the opening '{' of this group
      let start = idx;
      while (start > 0 && text[start] !== "{") start -= 1;
      if (text[start] !== "{") continue;
      text = removeGroupAt(text, start);
      changed = true;
      break;
    }
  }

  // Also remove generic {\* ...} destinations
  changed = true;
  while (changed) {
    changed = false;
    const idx = text.indexOf("{\\*");
    if (idx !== -1) {
      text = removeGroupAt(text, idx);
      changed = true;
    }
  }

  return text;
}

/**
 * Converts RTF (e.g. from legacy doctor notes) to plain text.
 * Non-RTF strings are returned unchanged.
 */
export function rtfToPlainText(input) {
  if (input == null) return "";
  const value = String(input);
  if (!/^\{\\rtf/i.test(value.trim())) {
    return value;
  }

  let text = stripRtfHeaderGroups(value);

  text = text
    .replace(/\\par[d]?/gi, "\n")
    .replace(/\\line/gi, "\n")
    .replace(/\\tab/gi, "\t")
    .replace(/\\'([0-9a-fA-F]{2})/g, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    )
    .replace(/\\u(-?\d+)\??/g, (_, num) => {
      const code = Number(num);
      if (!Number.isFinite(code) || code < 0) return "";
      return String.fromCharCode(code);
    })
    // Strip remaining control words (\fs18, \f2, \ltrch, \ansi, etc.)
    .replace(/\\[a-zA-Z]+(-?\d+)?[ ]?/g, "")
    .replace(/[{}]/g, "")
    .replace(/\\\\/g, "\\")
    .replace(/\r/g, "");

  return text
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

export default rtfToPlainText;
